;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/pair/:code\",\"regex\":\"^/pair/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/user/recover/:hash\",\"regex\":\"^/user/recover/([^/]+)$\",\"paramNames\":[\"hash\"],\"hasOptionalPrefix\":false},{\"path\":\"/redeem/:partner*\",\"regex\":\"^/redeem/(.+)$\",\"paramNames\":[\"partner\"],\"hasOptionalPrefix\":false},{\"path\":\"/:path*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"path\"],\"hasOptionalPrefix\":false},{\"path\":\"/checkout/:path*\",\"regex\":\"^/checkout/(.+)$\",\"paramNames\":[\"path\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/callback\"},{\"path\":\"/login\"},{\"path\":\"/pair\"},{\"path\":\"/register\"},{\"path\":\"/reset-password\"},{\"path\":\"/welcome\"},{\"path\":\"/content-detail\"},{\"path\":\"/event-detail\"},{\"path\":\"/see-more\"},{\"path\":\"/team-detail\"},{\"path\":\"/tournament-detail\"},{\"path\":\"/\"},{\"path\":\"/calendar\"},{\"path\":\"/channels\"},{\"path\":\"/favorite-team\"},{\"path\":\"/install-app\"},{\"path\":\"/my-team\"},{\"path\":\"/pick-team\"},{\"path\":\"/redeem\"},{\"path\":\"/special-offer\"},{\"path\":\"/success\"},{\"path\":\"/tournaments\"},{\"path\":\"/manage-subscription\"},{\"path\":\"/profile\"},{\"path\":\"/checkout\"},{\"path\":\"/checkout-v2\"},{\"path\":\"/player\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.1";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from '@sentry/nextjs'
import settings from '@/settings/public'
import { SENTRY_IGNORE_ERRORS } from './config/sentry-ignore-errors'
import { isChunkLoadError, shouldDropEvent } from './config/sentry-event-filter'

const environment = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'production'
const dsn = settings.sentry?.dsn || ''

// A dev machine running with NEXT_PUBLIC_APP_ENV=production (e.g. testing prod
// settings locally) would otherwise report Fast Refresh / HMR artifacts to the
// production Sentry project (see issue 7599980172). `next dev` always inlines
// NODE_ENV=development, and localhost catches local `next build && start`.
const isDevRuntime =
  process.env.NODE_ENV !== 'production' ||
  /^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(window.location.hostname)

// Outgoing fetch URLs whose spans we don't want recorded. Intentional patterns
// (streaming playlist fan-out, third-party tracker) that cause Sentry's N+1 API
// Call detector to fire repeatedly. Dropping the spans removes the noise without
// disabling the detector globally or suppressing errors on these routes.
const SKIP_SPAN_URL_PATTERNS = [
  /\/api\/generic\/playlists/i,
  /gnsnpaw\.com\/fanatiz\/decision/i,
  // Google Tag Gateway (FZSPF-3730): high-volume first-party gtm.js/beacon
  // traffic, same noise profile as the patterns above.
  /\/metrics\//i,
]

Sentry.init({
  dsn,
  environment,
  // Only send events from the real production environment. In development and
  // staging the SDK still initializes (so calls like captureException are safe
  // no-ops) but no events leave the browser.
  enabled: environment === 'production' && !isDevRuntime,
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  integrations: (integrations) => [
    ...integrations.filter((integration) => integration.name !== 'Replay'),
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest: (url) =>
        !SKIP_SPAN_URL_PATTERNS.some((re) => re.test(url)),
    }),
    // Surface non-standard own-properties on captured Errors (IError `code`,
    // `extra.info.transactionError`, and the Recurly `code`/`classification`
    // attached in useRecurlyToken) as Sentry "Additional Data". Without this,
    // captured API errors show only their userMessage (e.g. "Try again later")
    // and the actual failure reason never leaves the browser. depth covers the
    // nested extra.info.transactionError. See FZSPF-3679.
    Sentry.extraErrorDataIntegration({ depth: 5 }),
  ],
  denyUrls: [
    /https?:\/\/gnsnpaw\.com\/fanatiz\/decision/i,
    /consentmanager\.net/i,
    // Google Tag Gateway (FZSPF-3730): gtm.js is now served same-origin under
    // /metrics, so it would otherwise pass `allowUrls` below and surface
    // third-party GTM errors as if they were ours.
    /\/metrics\//i,
  ],
  // All event-drop rules live in ./config/sentry-event-filter (unit-tested).
  beforeSend: (event, hint) => (shouldDropEvent(event, hint) ? null : event),
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  sampleRate: 0.1,
  tracesSampleRate: 0.1,
  debug: environment === 'development',
  sendDefaultPii: false,
  allowUrls: [
    new RegExp(`^${window.location.origin.replace(/\./g, '\\.')}`),
  ],
})

// A ChunkLoadError means the browser tried to fetch a JS chunk that no longer
// exists — almost always a client holding stale chunk hashes after a new deploy.
// Force a single reload so it pulls the current chunks. Guarded by a
// sessionStorage timestamp so a genuinely-missing chunk (broken deploy) can't
// spin in a reload loop. See issue 7219543691.
const CHUNK_RELOAD_KEY = `chunk-load-reloaded-at:${environment}`
const CHUNK_RELOAD_COOLDOWN_MS = 10_000

const reloadOnceForChunkError = () => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
    // Already reloaded recently — the chunk is truly gone, don't loop.
    if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
  } catch {
    // sessionStorage blocked (private mode / locked-down WebView) — without a
    // loop guard a reload could spin, so skip it rather than risk a loop.
  }
}

window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason)) reloadOnceForChunkError()
})

window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.error) || isChunkLoadError(e.message)) reloadOnceForChunkError()
})

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
